import React, { useState, useEffect } from 'react';
import './RegisterForm.css';

// Force LTR on register page
const useForceLTR = () => {
  useEffect(() => {
    const html = document.documentElement;
    const originalDir = html.getAttribute('dir');
    html.setAttribute('dir', 'ltr');
    return () => {
      if (originalDir) html.setAttribute('dir', originalDir);
    };
  }, []);
};

const RegisterForm = ({ onRegister, onBack, onSuccess, t }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    inGameName: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [serverError, setServerError] = useState('');

  // Apply LTR for register page
  useForceLTR();

  // Validation rules
  const validateUsername = (value) => {
    if (!value.trim()) {
      return t('usernameRequired', 'Username is required');
    }
    if (value.length < 3) {
      return t('usernameTooShort', 'Username must be at least 3 characters');
    }
    if (value.length > 20) {
      return t('usernameTooLong', 'Username must be less than 20 characters');
    }
    // Check for Hebrew characters
    if (/[\u0590-\u05FF]/.test(value)) {
      return t('usernameNoHebrew', 'Username cannot contain Hebrew characters');
    }
    // Check for Arabic characters
    if (/[\u0600-\u06FF]/.test(value)) {
      return t('usernameNoArabic', 'Username cannot contain Arabic characters');
    }
    // Only allow alphanumeric, underscore, and dash
    if (!/^[a-zA-Z0-9_-]+$/.test(value)) {
      return t('usernameInvalidChars', 'Username can only contain letters, numbers, underscore and dash');
    }
    return '';
  };

  const validateEmail = (value) => {
    if (!value.trim()) {
      return t('emailRequired', 'Email is required');
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return t('emailInvalid', 'Please enter a valid email address');
    }
    return '';
  };

  const validateInGameName = (value) => {
    if (!value.trim()) {
      return t('inGameNameRequired', 'In-game name is required');
    }
    if (value.length < 2) {
      return t('inGameNameTooShort', 'In-game name must be at least 2 characters');
    }
    if (value.length > 30) {
      return t('inGameNameTooLong', 'In-game name must be less than 30 characters');
    }
    return '';
  };

  const validatePassword = (value) => {
    if (!value) {
      return t('passwordRequired', 'Password is required');
    }
    if (value.length < 6) {
      return t('passwordTooShort', 'Password must be at least 6 characters');
    }
    if (value.length > 50) {
      return t('passwordTooLong', 'Password must be less than 50 characters');
    }
    // Check for at least one number
    if (!/\d/.test(value)) {
      return t('passwordNeedsNumber', 'Password must contain at least one number');
    }
    // Check for at least one letter
    if (!/[a-zA-Z]/.test(value)) {
      return t('passwordNeedsLetter', 'Password must contain at least one letter');
    }
    return '';
  };

  const validateConfirmPassword = (value) => {
    if (!value) {
      return t('confirmPasswordRequired', 'Please confirm your password');
    }
    if (value !== formData.password) {
      return t('passwordsDoNotMatch', 'Passwords do not match');
    }
    return '';
  };

  // Validate all fields
  const validateField = (name, value) => {
    switch (name) {
      case 'username':
        return validateUsername(value);
      case 'email':
        return validateEmail(value);
      case 'inGameName':
        return validateInGameName(value);
      case 'password':
        return validatePassword(value);
      case 'confirmPassword':
        return validateConfirmPassword(value);
      default:
        return '';
    }
  };

  // Real-time validation on change
  useEffect(() => {
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      if (touched[key]) {
        const error = validateField(key, formData[key]);
        if (error) {
          newErrors[key] = error;
        }
      }
    });

    // Re-validate confirm password when password changes
    if (touched.confirmPassword && formData.confirmPassword) {
      const confirmError = validateConfirmPassword(formData.confirmPassword);
      if (confirmError) {
        newErrors.confirmPassword = confirmError;
      }
    }

    setErrors(newErrors);
  }, [formData, touched]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setServerError('');
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched
    const allTouched = {};
    Object.keys(formData).forEach(key => {
      allTouched[key] = true;
    });
    setTouched(allTouched);

    // Validate all fields
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) {
        newErrors[key] = error;
      }
    });
    setErrors(newErrors);

    // If there are errors, don't submit
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setLoading(true);
    setServerError('');

    try {
      const result = await onRegister({
        username: formData.username,
        email: formData.email,
        inGameName: formData.inGameName,
        password: formData.password
      });

      if (result && !result.success && result.message) {
        setServerError(result.message);
      } else if (onSuccess) {
        onSuccess(result);
      }
    } catch (error) {
      setServerError(error.message || t('registrationFailed', 'Registration failed. Please try again.'));
    }

    setLoading(false);
  };

  const isFormValid = Object.keys(formData).every(key => {
    return formData[key].trim() && !validateField(key, formData[key]);
  });

  const getInputClass = (fieldName) => {
    let className = 'register-input';
    if (touched[fieldName]) {
      if (errors[fieldName]) {
        className += ' input-error';
      } else if (formData[fieldName].trim()) {
        className += ' input-valid';
      }
    }
    return className;
  };

  return (
    <div className="register-container" dir="ltr">
      <div className="register-form-wrapper">
        <div className="register-header">
          <h2 className="register-title">
            {t('createAccount', 'Create Account')}
          </h2>
          <p className="register-subtitle">
            {t('fillAllFields', 'Fill in all fields to create your account')}
          </p>
        </div>

        {serverError && (
          <div className="server-error">
            <span className="error-icon">⚠️</span>
            {serverError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="register-form">
          {/* Username */}
          <div className="input-group">
            <label className="input-label">{t('username', 'Username')}</label>
            <input lang="en" dir="ltr" style={{textAlign: "center", direction: "ltr"}}
              type="text"
              name="username"
              placeholder={t('enterUsername', 'Enter username (letters, numbers, _ -)')}
              value={formData.username}
              onChange={handleChange}
              onBlur={handleBlur}
              className={getInputClass('username')}
              disabled={loading}
              autoComplete="username"
            />
            {touched.username && errors.username && (
              <span className="field-error">{errors.username}</span>
            )}
            {touched.username && !errors.username && formData.username.trim() && (
              <span className="field-valid">✓ {t('usernameValid', 'Username is valid')}</span>
            )}
          </div>

          {/* Email */}
          <div className="input-group">
            <label className="input-label">{t('email', 'Email')}</label>
            <input lang="en" dir="ltr" style={{textAlign: "center", direction: "ltr"}}
              type="email"
              name="email"
              placeholder={t('enterEmail', 'Enter your email address')}
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              className={getInputClass('email')}
              disabled={loading}
              autoComplete="email"
            />
            {touched.email && errors.email && (
              <span className="field-error">{errors.email}</span>
            )}
            {touched.email && !errors.email && formData.email.trim() && (
              <span className="field-valid">✓ {t('emailValid', 'Email is valid')}</span>
            )}
          </div>

          {/* In-Game Name */}
          <div className="input-group">
            <label className="input-label">{t('inGameName', 'In-Game Name')}</label>
            <input lang="en" dir="ltr" style={{textAlign: "center", direction: "ltr"}}
              type="text"
              name="inGameName"
              placeholder={t('enterInGameName', 'Enter your in-game name')}
              value={formData.inGameName}
              onChange={handleChange}
              onBlur={handleBlur}
              className={getInputClass('inGameName')}
              disabled={loading}
            />
            {touched.inGameName && errors.inGameName && (
              <span className="field-error">{errors.inGameName}</span>
            )}
            {touched.inGameName && !errors.inGameName && formData.inGameName.trim() && (
              <span className="field-valid">✓ {t('inGameNameValid', 'In-game name is valid')}</span>
            )}
          </div>

          {/* Password */}
          <div className="input-group">
            <label className="input-label">{t('password', 'Password')}</label>
            <input lang="en" dir="ltr" style={{textAlign: "center", direction: "ltr"}}
              type="password"
              name="password"
              placeholder={t('enterPassword', 'Enter password (min 6 chars, letter + number)')}
              value={formData.password}
              onChange={handleChange}
              onBlur={handleBlur}
              className={getInputClass('password')}
              disabled={loading}
              autoComplete="new-password"
            />
            {touched.password && errors.password && (
              <span className="field-error">{errors.password}</span>
            )}
            {touched.password && !errors.password && formData.password && (
              <span className="field-valid">✓ {t('passwordValid', 'Password is valid')}</span>
            )}
            <div className="password-requirements">
              <span className={formData.password.length >= 6 ? 'req-met' : 'req-unmet'}>
                • {t('min6Chars', 'At least 6 characters')}
              </span>
              <span className={/[a-zA-Z]/.test(formData.password) ? 'req-met' : 'req-unmet'}>
                • {t('hasLetter', 'Contains a letter')}
              </span>
              <span className={/\d/.test(formData.password) ? 'req-met' : 'req-unmet'}>
                • {t('hasNumber', 'Contains a number')}
              </span>
            </div>
          </div>

          {/* Confirm Password */}
          <div className="input-group">
            <label className="input-label">{t('confirmPassword', 'Confirm Password')}</label>
            <input lang="en" dir="ltr" style={{textAlign: "center", direction: "ltr"}}
              type="password"
              name="confirmPassword"
              placeholder={t('reenterPassword', 'Re-enter your password')}
              value={formData.confirmPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              className={getInputClass('confirmPassword')}
              disabled={loading}
              autoComplete="new-password"
            />
            {touched.confirmPassword && errors.confirmPassword && (
              <span className="field-error">{errors.confirmPassword}</span>
            )}
            {touched.confirmPassword && !errors.confirmPassword && formData.confirmPassword && (
              <span className="field-valid">✓ {t('passwordsMatch', 'Passwords match')}</span>
            )}
          </div>

          <button
            type="submit"
            disabled={loading || !isFormValid}
            className="register-button"
          >
            {loading ? (
              <>
                <span className="loading-spinner"></span>
                {t('creatingAccount', 'Creating Account...')}
              </>
            ) : (
              t('register', 'Register')
            )}
          </button>

          <button
            type="button"
            onClick={onBack}
            disabled={loading}
            className="back-button"
          >
            {t('backToLogin', 'Back to Login')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default RegisterForm;
